import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest, silentRequest } from './authConfig';
import { isAgentSandbox } from '../appRuntimeMode';

const $ = window.$;
const TScMessenger = window.TScMessenger;

let accountId = '';
let myMSALObj = null;
let isAuthenticated = false;
let curMsalAuthOp = null;
let locallyUnavailable = false;

function getAuthContext() {
    return myMSALObj;
}

function hasClientId() {
    return typeof msalConfig.auth.clientId === 'string' && msalConfig.auth.clientId.trim() !== '';
}

function isAvailable() {
    return !isAgentSandbox() || (hasClientId() && !locallyUnavailable);
}

function isCurrent(operation) {
    return curMsalAuthOp === operation;
}

function rejectOperation(operation, error, providerUnavailable = false) {
    if (!isCurrent(operation)) return;
    clearTimeout(operation.timeout);
    curMsalAuthOp = null;
    // A failed or timed-out client must not be reused by the next request.
    myMSALObj = null;
    // An unsuccessful sign-in is retryable through the configured provider's UI.
    // Only configuration or SDK setup failures make the provider unavailable.
    if (operation.local && providerUnavailable) locallyUnavailable = true;
    operation.dfd.reject(error);
}

function setupAndInitialiseMsalPublicClientApplication(isReturningUser = false) {
    if (curMsalAuthOp) return curMsalAuthOp.dfd.promise();

    const operation = { dfd: $.Deferred(), local: isAgentSandbox(), isReturningUser };
    const promise = operation.dfd.promise();
    curMsalAuthOp = operation;
    if (operation.local && !hasClientId()) {
        rejectOperation(operation, new Error('Microsoft sign-in is unavailable'), true);
        return promise;
    }

    if (operation.local) {
        operation.timeout = setTimeout(() => {
            rejectOperation(operation, new Error('Microsoft sign-in timed out'));
        }, 20000);
    }
    try {
        if (!myMSALObj) myMSALObj = new PublicClientApplication(msalConfig);
        operation.client = myMSALObj;
        Promise.resolve(operation.client.initialize())
            .then(() => {
                if (!isCurrent(operation)) return;
                operation.initialized = true;
                locallyUnavailable = false;
                return operation.client.handleRedirectPromise();
            })
            .then(response => handleResponse(response, operation))
            .catch(error => rejectOperation(operation, error, !operation.initialized));
    } catch (error) {
        rejectOperation(operation, error, true);
    }
    return promise;
}

function handleResponse(response, operation) {
    // SDK work cannot be cancelled reliably. Ignore callbacks from settled attempts,
    // including before writing hints, active accounts or authentication state.
    if (!isCurrent(operation)) return;
    TScMessenger.writeDebugMessage('auth.js, msalAuth handleResponse');
    if (response != null) {
        const account = response.account;
        operation.client.setActiveAccount(account);
        localStorage.setItem('loginHint', account.idTokenClaims.login_hint);
        accountId = account.homeAccountId;
        isAuthenticated = true;
        locallyUnavailable = false;
        clearTimeout(operation.timeout);
        curMsalAuthOp = null;
        operation.dfd.resolve({
            msalObj: operation.client,
            accountId,
            email: account.username,
            accessToken: response.accessToken,
            idToken: response.idToken,
            fullName: account.name
        });
        return;
    }

    const currentAccounts = operation.client.getAllAccounts();
    let request;
    if (currentAccounts && currentAccounts.length === 1) {
        request = () => operation.client.acquireTokenSilent(loginRequest);
    } else if (localStorage.getItem('loginHint')) {
        request = () => operation.client.ssoSilent(silentRequest);
    } else {
        fallbackToSignIn(operation);
        return;
    }
    Promise.resolve().then(() => {
        if (isCurrent(operation)) return request();
    }).then(response => handleTokenResponse(response, operation))
        .catch(error => fallbackToSignIn(operation, error));
}

function handleTokenResponse(response, operation) {
    if (!isCurrent(operation)) return;
    if (response == null) {
        rejectOperation(operation, new Error('Microsoft sign-in returned no identity'));
    } else {
        handleResponse(response, operation);
    }
}

function fallbackToSignIn(operation, error) {
    if (!isCurrent(operation)) return;
    // Local reloads settle at the sign-in page. Only an explicit provider choice
    // may launch an interactive login. Configured nonlocal flows keep their popup.
    if (operation.local && operation.isReturningUser) {
        rejectOperation(operation, error || new Error('Microsoft sign-in requires interaction'));
    } else {
        signIn(operation);
    }
}

function signIn(operation = curMsalAuthOp) {
    if (!operation) return setupAndInitialiseMsalPublicClientApplication();
    if (!isCurrent(operation)) return;
    return Promise.resolve().then(() => {
        if (isCurrent(operation)) return operation.client.loginPopup(loginRequest);
    }).then(response => handleTokenResponse(response, operation))
        .catch(error => rejectOperation(operation, error));
}

export {
    isAuthenticated,
    isAvailable,
    getAuthContext,
    setupAndInitialiseMsalPublicClientApplication,
    signIn
};
